import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import estimateProvider from "@/providers/estimate.provider";
import type { Estimation } from "@/interfaces/estimation.interface";
import { ESTIMATE_KEYS } from "@/hooks/queries/estimates/queryKeys";

export interface UpdateEstimateFieldMutationParams {
  estimateId: string;
  updates: Partial<Estimation>;
}

export function useUpdateEstimateFieldMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateEstimateFieldMutationParams) => {
      const { success, data } = await estimateProvider.updateEstimateField({
        estimaticsData: {
          estimateId: params.estimateId,
          ...params.updates,
        },
      });

      if (!success) {
        throw new Error("Failed to update estimate");
      }

      return { success, data };
    },
    onError: (error) => {
      toast.error("Failed to update estimate");
      console.error("Update estimate field error:", error);
    },
    /** Refetch metadata after a successful write; avoid invalidating `detail` here (line items) — that caused refetch storms and Radix Select no-op saves. Call sites refresh lines when needed (e.g. Cat 3 boundary). */
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ESTIMATE_KEYS.detailMetadata(variables.estimateId),
      });
    },
  });
}
